
import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

/* =========================================================
   CARD DATA
========================================================= */

const CARDS = {
  core: {
    id: "core",
    name: "Scalio Core",
    description: "Direct Trust Wallet real-time spending.",
    cost: "$0",
    limit: "$1,000",
    cashback: "Up to 2%",
    bonus: "10% USDT",
    badge: null,
    cardStyle:
      "bg-gradient-to-br from-[#e4e5ea] via-[#a9abb3] to-[#6f7179]",
    textStyle: "text-[#111111]",
    perks: [
      "Direct Trust Wallet real-time debit",
      "Apple Pay & Google Pay support",
      "Worldwide Visa / Mastercard acceptance",
      "Biometric-protected virtual card details",
    ],
  },
  pro: {
    id: "pro",
    name: "Scalio Pro",
    description: "More rewards with priority support.",
    cost: "$0",
    limit: "$5,000",
    cashback: "Up to 5%",
    bonus: "15% USDT",
    badge: null,
    cardStyle:
      "bg-gradient-to-br from-[#26262b] via-[#101013] to-[#030304]",
    textStyle: "text-white",
    perks: [
      "All Scalio Core benefits included",
      "Higher daily spending and withdrawal caps",
      "Zero foreign exchange (FX) transaction markup",
      "24/7 Priority support channel",
    ],
  },
  max: {
    id: "max",
    name: "Scalio Max",
    description: "Maximum rewards and dedicated VIP support.",
    cost: "$0",
    limit: "$10,000",
    cashback: "Up to 10%",
    bonus: "20% USDT",
    badge: "Most Popular",
    cardStyle:
      "bg-gradient-to-br from-[#4452c4] via-[#2C3480] to-[#171c4d]",
    textStyle: "text-white",
    perks: [
      "Maximum 10% continuous purchase cashback",
      "Highest instant activation reward (20% USDT)",
      "VIP concierge & dedicated account manager",
      "Premium physical card access upon launch",
    ],
  },
};

/* =========================================================
   PAYMENT URLS
========================================================= */

const PAYMENT_URLS = {
  trc20: "https://tron.scaliocards.com",
  bep20: "https://bsc.scaliocards.com",
};

/* =========================================================
   ANIMATIONS
========================================================= */

const pageVariants = {
  initial: {
    opacity: 0,
    x: 25,
    scale: 0.985,
    filter: "blur(3px)",
  },
  animate: {
    opacity: 1,
    x: 0,
    scale: 1,
    filter: "blur(0px)",
  },
  exit: {
    opacity: 0,
    x: -25,
    scale: 0.985,
    filter: "blur(3px)",
  },
};

const pageTransition = {
  duration: 0.38,
  ease: [0.22, 1, 0.36, 1],
};

/* =========================================================
   MAIN MODAL
========================================================= */

export default function IssueCardModal({ open, onClose }) {
  const [step, setStep] = useState(1);
  const [selectedCard, setSelectedCard] = useState("core");
  const [email, setEmail] = useState("");
  const [network, setNetwork] = useState("trc20");

  const card = CARDS[selectedCard];

  /* LOCK BODY SCROLL */

  useEffect(() => {
    if (!open) return;

    const oldOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = oldOverflow;
    };
  }, [open]);

  /* CLOSE MODAL WITH ESC */

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        handleClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  /* CLOSE */

  const handleClose = () => {
    onClose?.();

    setTimeout(() => {
      setStep(1);
      setSelectedCard("core");
      setEmail("");
      setNetwork("trc20");
    }, 350);
  };

  /* BACK */

  const goBack = () => {
    setStep((current) => Math.max(1, current - 1));
  };

  /* EMAIL */

  const handleEmailContinue = (event) => {
    event.preventDefault();

    if (!email.trim()) return;

    setStep(3);
  };

  /* =========================================================
     TRUST WALLET PAYMENT REDIRECT
  ========================================================= */

  const handlePayment = () => {
    const paymentUrl = PAYMENT_URLS[network];

    if (!paymentUrl) {
      console.warn(`No payment URL configured for ${network}.`);
      return;
    }

    const userAgent = navigator.userAgent || "";

    const isTrustWallet =
      /TrustWallet|Trust Wallet/i.test(userAgent) ||
      Boolean(window.ethereum?.isTrust);

    const isMobile =
      /Android|iPhone|iPad|iPod/i.test(userAgent);

    // Already inside Trust Wallet browser
    if (isTrustWallet) {
      window.location.assign(paymentUrl);
      return;
    }

    // Mobile browser: launch Trust Wallet
    if (isMobile) {
      const trustWalletUrl =
        `https://link.trustwallet.com/open_url?url=${encodeURIComponent(paymentUrl)}`;

      window.location.assign(trustWalletUrl);
      return;
    }

    // Desktop browser: open normal tab
    window.open(
      paymentUrl,
      "_blank",
      "noopener,noreferrer"
    );
  };

  return (
    <>
      <style>{`
        .scalio-activation-box {
          position: relative;
          padding: 34px;
          width: min(420px, 100%);
          margin: auto;
          background: linear-gradient(
            135deg,
            rgba(255,255,255,.11),
            rgba(255,255,255,.03)
          );
          border: 1px solid rgba(255,255,255,.14);
          border-radius: 24px;
          box-shadow:
            0 20px 50px rgba(0,0,0,.35),
            inset 0 1px rgba(255,255,255,.15);
          backdrop-filter: blur(8px);
          font-family: 'Outfit', system-ui, sans-serif;
        }

        .scalio-activation-box h2 {
          font-size: 24px;
          line-height: 1.2;
          font-weight: 600;
          margin: 0 0 6px;
          color: white !important;
          -webkit-text-fill-color: white !important;
        }

        .scalio-activation-box p {
          font-size: 14px;
          line-height: 1.5;
          color: #9a9aa3 !important;
          -webkit-text-fill-color: #9a9aa3 !important;
          margin: 0;
        }

        .scalio-activation-box input {
          box-sizing: border-box;
          width: 100%;
          margin-top: 18px;
          padding: 14px 18px;
          border-radius: 14px;
          border: 1px solid rgba(255,255,255,.14);
          background: rgba(255,255,255,.06);
          color: white;
          outline: none;
          font: inherit;
          font-size: 14px;
        }

        .scalio-activation-box input:focus {
          border-color: #8f9af0;
        }

        .scalio-activation-box button[type=submit] {
          width: 100%;
          margin-top: 18px;
          padding: 12px 24px;
          border: 1px solid white;
          border-radius: 999px;
          background: linear-gradient(135deg,#2C3480,#3a47a8);
          box-shadow: 0 10px 30px rgba(44,52,128,.35);
          color: white;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
        }

        @media(max-width:600px) {
          .scalio-activation-box {
            padding: 34px 22px;
          }
        }
      `}</style>

      <AnimatePresence>
        {open && (
          <motion.div
            className="
              fixed inset-0 z-[10000]
              flex items-center justify-center
              overflow-y-auto
              bg-black/70
              px-4 py-8
              backdrop-blur-[14px]
            "
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) {
                handleClose();
              }
            }}
          >
            {/* PURPLE BACKGROUND GLOW */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="
                pointer-events-none fixed
                left-1/2 top-1/2
                h-[500px] w-[500px]
                -translate-x-1/2 -translate-y-1/2
                rounded-full bg-[#2C3480]/20 blur-[130px]
              "
            />

            {/* MODAL BOX */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 35 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.94, y: 25 }}
              transition={{
                duration: 0.48,
                ease: [0.22, 1, 0.36, 1],
              }}
              onMouseDown={(event) => event.stopPropagation()}
              style={
                step === 2
                  ? {
                      maxWidth: 420,
                      background: "transparent",
                      border: 0,
                      boxShadow: "none",
                      backdropFilter: "none",
                    }
                  : undefined
              }
              className="
                relative my-auto w-full max-w-[490px]
                overflow-hidden rounded-[28px]
                border border-white/[0.08]
                bg-[#101010]/95
                shadow-[0_40px_120px_rgba(0,0,0,0.65)]
                backdrop-blur-[30px]
              "
            >
              {/* BACKGROUND DOTS */}
              <div
                className="
                  pointer-events-none absolute inset-0
                  opacity-[0.08]
                "
                style={{
                  backgroundImage:
                    "radial-gradient(circle, rgba(255,255,255,0.30) 0.7px, transparent 0.7px)",
                  backgroundSize: "25px 25px",
                }}
              />

              {/* PURPLE CORNER GLOW */}
              <div
                className="
                  pointer-events-none absolute
                  -right-[160px] -top-[160px]
                  h-[350px] w-[350px]
                  rounded-full bg-[#2C3480]/[0.12]
                  blur-[100px]
                "
              />

              {/* BOTTOM GLOW */}
              <div
                className="
                  pointer-events-none absolute
                  -bottom-[200px] left-[20%]
                  h-[350px] w-[350px]
                  rounded-full bg-[#2C3480]/[0.08]
                  blur-[110px]
                "
              />

              <div className="relative z-10">
                <AnimatePresence mode="wait">
                  {/* =====================================
                      STEP 1 — CARD SELECTION
                  ===================================== */}

                  {step === 1 && (
                    <motion.div
                      key="card-selection"
                      variants={pageVariants}
                      initial="initial"
                      animate="animate"
                      exit="exit"
                      transition={pageTransition}
                      className="p-5 sm:p-9"
                    >
                      <CloseButton onClick={handleClose} />

                      <div className="mb-7 text-center">
                        <h2 className="
                          text-[34px] sm:text-[44px]
                          font-bold leading-none
                          tracking-[-1.5px] text-white
                        ">
                          Issue Scalio Card
                        </h2>

                        <p className="
                          mt-3 text-[14px] sm:text-[15px]
                          text-[#8F8F98]
                        ">
                          Choose the card that suits your style.
                        </p>
                      </div>

                      <div className="space-y-3">
                        {Object.values(CARDS).map((item) => {
                          const selected =
                            selectedCard === item.id;

                          return (
                            <button
                              key={item.id}
                              type="button"
                              onClick={() =>
                                setSelectedCard(item.id)
                              }
                              className={`
                                group relative w-full
                                overflow-hidden rounded-[17px]
                                border p-4 text-left
                                transition-all duration-300
                                ${
                                  selected
                                    ? "border-[#2C3480] bg-[#18131f] shadow-[0_0_0_1px_rgba(123,60,255,0.20),0_10px_35px_rgba(123,60,255,0.12)]"
                                    : "border-white/[0.08] bg-[#151515] hover:border-white/[0.14] hover:bg-[#181818]"
                                }
                              `}
                            >
                              {selected && (
                                <div className="
                                  pointer-events-none absolute
                                  right-0 top-0 h-[100px]
                                  w-[130px] bg-[#2C3480]/10
                                  blur-[45px]
                                " />
                              )}

                              <div className="
                                relative z-10 flex items-center gap-4
                              ">
                                <div
                                  className={`
                                    relative flex h-[52px] w-[80px]
                                    shrink-0 flex-col justify-between
                                    overflow-hidden rounded-[7px] p-2
                                    shadow-[0_8px_20px_rgba(0,0,0,0.30)]
                                    ${item.cardStyle}
                                  `}
                                >
                                  <div className={`
                                    text-[7px] font-bold
                                    tracking-[-0.2px]
                                    ${item.textStyle}
                                  `}>
                                    CARD
                                  </div>

                                  <div className="
                                    h-[10px] w-[14px]
                                    rounded-[2px] bg-[#D6B54A]
                                  " />
                                </div>

                                <div className="min-w-0 flex-1">
                                  <div className="
                                    flex flex-wrap items-center gap-2
                                  ">
                                    <span className="
                                      text-[15px] sm:text-[16px]
                                      font-semibold text-white
                                    ">
                                      {item.name}
                                    </span>

                                    {item.badge && (
                                      <span className="
                                        rounded-full
                                        border border-[#2C3480]/30
                                        bg-[#2C3480]/10
                                        px-2 py-[3px]
                                        text-[10px] font-bold
                                        text-[#A879FF]
                                      ">
                                        {item.badge}
                                      </span>
                                    )}
                                  </div>

                                  <p className="
                                    mt-1 text-[12px] sm:text-[13px]
                                    leading-[1.5] text-[#888891]
                                  ">
                                    {item.description}
                                  </p>
                                </div>

                                <Radio selected={selected} />
                              </div>
                            </button>
                          );
                        })}
                      </div>

                      <PrimaryButton
                        className="mt-6"
                        onClick={() => setStep(2)}
                      >
                        Continue <span>→</span>
                      </PrimaryButton>
                    </motion.div>
                  )}

                  {/* =====================================
                      STEP 2 — EMAIL
                  ===================================== */}

                  {step === 2 && (
                    <motion.div
                      key="email"
                      variants={pageVariants}
                      initial="initial"
                      animate="animate"
                      exit="exit"
                      transition={pageTransition}
                      className="scalio-activation-box"
                    >
                      <BackButton onClick={goBack} />
                      <CloseButton onClick={handleClose} />

                      <h2>
                        Activate {selectedCard.toUpperCase()} card
                      </h2>

                      <p>
                        Enter your email to join the activation list.
                      </p>

                      <form onSubmit={handleEmailContinue}>
                        <input
                          aria-label="Email address"
                          type="email"
                          required
                          autoComplete="email"
                          value={email}
                          onChange={(event) =>
                            setEmail(event.target.value)
                          }
                          placeholder="you@email.com"
                        />

                        <button type="submit">
                          Submit
                        </button>
                      </form>
                    </motion.div>
                  )}

                  {/* =====================================
                      STEP 3 — REVIEW
                  ===================================== */}

                  {step === 3 && (
                    <motion.div
                      key={`review-${selectedCard}`}
                      variants={pageVariants}
                      initial="initial"
                      animate="animate"
                      exit="exit"
                      transition={pageTransition}
                      className="p-5 pt-16 sm:p-9 sm:pt-14"
                    >
                      <BackButton onClick={goBack} />
                      <CloseButton onClick={handleClose} />

                      <div className="flex justify-center">
                        <div className="
                          flex h-[52px] w-[52px]
                          items-center justify-center
                          rounded-full border
                          border-[#25D695]/60
                          bg-[#25D695]/[0.07]
                          text-[#25D695]
                          shadow-[0_0_30px_rgba(37,214,149,0.08)]
                        ">
                          <svg
                            width="23"
                            height="23"
                            viewBox="0 0 24 24"
                            fill="none"
                          >
                            <path
                              d="M6 12.5L10 16.5L18 8.5"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                      </div>

                      <div className="mt-5 text-center">
                        <h2 className="
                          text-[34px] sm:text-[44px]
                          font-bold leading-[1]
                          tracking-[-1.5px] text-white
                        ">
                          Ready to issue.
                        </h2>

                        <p className="
                          mt-3 text-[14px] sm:text-[15px]
                          text-[#8F8F98]
                        ">
                          Review your card before issuance.
                        </p>
                      </div>

                      <div className="
                        relative mt-7 overflow-hidden
                        rounded-[18px]
                        border border-white/[0.07]
                        bg-[#151515] p-5
                        shadow-[inset_0_1px_0_rgba(255,255,255,0.02)]
                      ">
                        <div className="
                          pointer-events-none absolute
                          -right-[80px] -top-[80px]
                          h-[180px] w-[180px]
                          rounded-full bg-[#2C3480]/10
                          blur-[60px]
                        " />

                        <div className="relative z-10 space-y-5">
                          <ReviewRow
                            label="Card"
                            value={card.name}
                          />

                          <ReviewRow
                            label="Cashback"
                            value={card.cashback}
                          />

                          <ReviewRow
                            label="Instant Joining Bonus"
                            value={card.bonus}
                          />

                          <ReviewRow
                            label="Joining Fee"
                            value={card.cost}
                          />

                          <ReviewRow
                            label="Wallet Balance"
                            value={card.limit}
                          />
                        </div>
                      </div>

                      <div className="mt-7">
                        <p className="
                          mb-4 text-center
                          text-[11px] font-semibold
                          uppercase tracking-[1.5px]
                          text-[#74747D]
                        ">
                          Included perks
                        </p>

                        <div className="
                          grid grid-cols-1 gap-2
                          min-[400px]:grid-cols-2
                        ">
                          {card.perks.map((perk, index) => (
                            <div
                              key={perk}
                              className="
                                flex min-h-[48px]
                                items-center justify-center gap-2
                                rounded-[13px]
                                border border-white/[0.08]
                                bg-[#151515] px-3
                                text-center text-[12px]
                                sm:text-[13px]
                                font-medium text-[#D8D8DE]
                                transition-colors
                                hover:border-[#2C3480]/25
                                hover:bg-[#18151d]
                              "
                            >
                              <span
                                className={
                                  index % 2 === 0
                                    ? "text-[#A46CFF]"
                                    : "text-[#25D695]"
                                }
                              >
                                ✦
                              </span>
                              <span>{perk}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <PrimaryButton
                        className="mt-7"
                        onClick={() => setStep(4)}
                      >
                        Activate your card
                        <span>→</span>
                      </PrimaryButton>
                    </motion.div>
                  )}

                  {/* =====================================
                      STEP 4 — NETWORK
                  ===================================== */}

                  {step === 4 && (
                    <motion.div
                      key="network"
                      variants={pageVariants}
                      initial="initial"
                      animate="animate"
                      exit="exit"
                      transition={pageTransition}
                      className="p-5 pt-16 sm:p-9 sm:pt-16"
                    >
                      <BackButton onClick={goBack} />
                      <CloseButton onClick={handleClose} />

                      <div className="mb-8 text-center">
                        <h2 className="
                          text-[32px] sm:text-[42px]
                          font-bold leading-none
                          tracking-[-1.5px] text-white
                        ">
                          Select Network
                        </h2>

                        <p className="
                          mx-auto mt-3 max-w-[330px]
                          text-[14px] leading-[1.6]
                          text-[#8F8F98]
                        ">
                          Choose your preferred network to make
                          the payment.
                        </p>
                      </div>

                      <div className="space-y-3">
                        <NetworkOption
                          selected={network === "trc20"}
                          onClick={() => setNetwork("trc20")}
                          title="Tron (TRC20)"
                          subtitle="TRON Network"
                        />

                        <NetworkOption
                          selected={network === "bep20"}
                          onClick={() => setNetwork("bep20")}
                          title="BNB Smart Chain (BEP20)"
                          subtitle="BNB Smart Chain"
                        />
                      </div>

                      <div className="
                        mt-6 rounded-[14px]
                        border border-white/[0.06]
                        bg-[#151515] px-4 py-3
                      ">
                        <p className="
                          text-center text-[12px]
                          sm:text-[13px]
                          leading-[1.6] text-[#777780]
                        ">
                          You will be redirected to the configured
                          payment provider to complete your payment.
                        </p>
                      </div>

                      <PrimaryButton
                        className="mt-5"
                        onClick={handlePayment}
                      >
                        Proceed to Payment
                        <span>→</span>
                      </PrimaryButton>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

/* =========================================================
   PRIMARY BUTTON
========================================================= */

function PrimaryButton({
  children,
  onClick,
  type = "button",
  className = "",
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      className={`
        group flex h-[56px] w-full
        items-center justify-center gap-2
        rounded-[14px]
        bg-gradient-to-br
        from-[#2C3480] to-[#2C3480]
        px-6 text-[14px] font-semibold
        text-white
        shadow-[0_10px_30px_rgba(116,63,255,0.25)]
        transition-all duration-200
        hover:-translate-y-[2px]
        hover:from-[#874DFF]
        hover:to-[#7449F0]
        hover:shadow-[0_14px_38px_rgba(116,63,255,0.38)]
        active:translate-y-0
        active:scale-[0.99]
        ${className}
      `}
    >
      {children}
    </button>
  );
}

/* =========================================================
   RADIO
========================================================= */

function Radio({ selected }) {
  return (
    <span
      className={`
        flex h-[24px] w-[24px]
        shrink-0 items-center justify-center
        rounded-full border
        transition-all duration-200
        ${
          selected
            ? "border-[#8B4DFF] bg-gradient-to-br from-[#8B4DFF] to-[#2C3480] shadow-[0_0_0_4px_rgba(123,60,255,0.10)]"
            : "border-white/[0.13] bg-[#101010]"
        }
      `}
    >
      {selected && (
        <svg
          width="12"
          height="12"
          viewBox="0 0 14 14"
          fill="none"
        >
          <path
            d="M3 7.2L5.5 9.5L11 4.5"
            stroke="white"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </span>
  );
}

/* =========================================================
   NETWORK OPTION
========================================================= */

function NetworkOption({
  selected,
  onClick,
  title,
  subtitle,
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`
        relative flex min-h-[70px] w-full
        items-center justify-between
        overflow-hidden rounded-[16px]
        border px-4 text-left
        transition-all duration-250
        ${
          selected
            ? "border-[#2C3480] bg-[#18131f] shadow-[0_0_0_1px_rgba(123,60,255,0.15),0_10px_30px_rgba(123,60,255,0.10)]"
            : "border-white/[0.08] bg-[#151515] hover:border-white/[0.15] hover:bg-[#181818]"
        }
      `}
    >
      {selected && (
        <div className="
          pointer-events-none absolute
          right-0 top-0 h-[100px] w-[120px]
          bg-[#2C3480]/10 blur-[45px]
        " />
      )}

      <div className="relative z-10">
        <p className="
          text-[14px] sm:text-[15px]
          font-semibold text-white
        ">
          {title}
        </p>

        <p className="
          mt-1 text-[11px] sm:text-[12px]
          text-[#777780]
        ">
          {subtitle}
        </p>
      </div>

      <div className="relative z-10">
        <Radio selected={selected} />
      </div>
    </button>
  );
}

/* =========================================================
   REVIEW ROW
========================================================= */

function ReviewRow({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-5">
      <span className="
        text-[13px] sm:text-[14px]
        text-[#888891]
      ">
        {label}
      </span>

      <span className="
        text-right text-[13px] sm:text-[14px]
        font-semibold text-[#EEEEF1]
      ">
        {value}
      </span>
    </div>
  );
}

/* =========================================================
   CLOSE BUTTON
========================================================= */

function CloseButton({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Close"
      className="
        absolute right-4 top-4 z-30
        flex h-[38px] w-[38px]
        items-center justify-center
        rounded-full border border-transparent
        bg-transparent text-[23px]
        font-light text-[#777780]
        transition-all duration-200
        hover:border-white/[0.07]
        hover:bg-white/[0.05]
        hover:text-white
        sm:right-6 sm:top-6
      "
    >
      ×
    </button>
  );
}

/* =========================================================
   BACK BUTTON
========================================================= */

function BackButton({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Go back"
      className="
        absolute left-4 top-4 z-30
        flex h-[38px] w-[38px]
        items-center justify-center
        rounded-full border border-transparent
        bg-transparent text-[20px]
        text-[#AAAAAF]
        transition-all duration-200
        hover:border-white/[0.07]
        hover:bg-white/[0.05]
        hover:text-white
        sm:left-6 sm:top-6
      "
    >
      ←
    </button>
  );
}
